import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bProductCatalogProducts } from '@cart-services/hooks/fp-b2b/use-fp-b2b-product-catalog-products'
import { type MergedCoupon } from '@cart-services/types'
import { type CountryCode } from '@constants'
import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { useMemo } from 'react'
import { type ProductCardEntry } from '../types/product-card-entry'
import { getCouponEligibleProductCards, getHighlightCoupons } from '../utils/brand-promo-transformer'

const PRODUCT_DISPLAY_LIMIT = 20

interface UseBrandPromoFilteredProductsProps {
  campaign: CrossBrandMallHomeCampaign
  countryCode: CountryCode
  userSelectedCouponId: number | null
}

interface UseBrandPromoFilteredProductsResult {
  highlightCoupons: MergedCoupon[]
  selectedCouponId: number | null
  productCardEntries: ProductCardEntry[]
  isDisplayLimited: boolean
  isCouponsLoading: boolean
  isProductsLoading: boolean
}


export const useBrandPromoFilteredProducts = (
  props: UseBrandPromoFilteredProductsProps,
): UseBrandPromoFilteredProductsResult => {
  const { campaign, countryCode, userSelectedCouponId } = props

  const { mergedCouponMap } = useFpB2bCoupons(
    campaign.campaign_id,
    campaign.project_code,
    countryCode,
    campaign.code,
    Platform.STM,
  )

  const { productCatalogDto, isValidating: isCatalogValidating, error: catalogError } = useFpB2bProductCatalogProducts(
    campaign.campaign_id,
    campaign.project_code,
    campaign.code,
    Platform.STM,
  )

  const highlightCoupons = useMemo<MergedCoupon[]>(
    () => getHighlightCoupons({ campaign, mergedCouponMap }),
    [campaign, mergedCouponMap],
  )

  /**
   * `selectedCouponId` 為 derived：使用者選擇若仍存在於 highlightCoupons 中即沿用，
   * 否則 fallback 到 highlightCoupons 第一張，避免指向已過濾的券導致商品清單為空。
  */
  const selectedCouponId = useMemo<number | null>(() => {
    const isUserSelectionValid = userSelectedCouponId !== null
      && highlightCoupons.some(coupon => coupon.coupon_id === userSelectedCouponId)
    if (isUserSelectionValid) return userSelectedCouponId
    return highlightCoupons[0]?.coupon_id ?? null
  }, [userSelectedCouponId, highlightCoupons])

  const eligibleProductCards = useMemo<ProductCardEntry[]>(
    () => getCouponEligibleProductCards({
      productCatalogDto,
      mergedCouponMap,
      selectedCouponId,
    }),
    [productCatalogDto, selectedCouponId, mergedCouponMap],
  )

  return {
    highlightCoupons,
    selectedCouponId,
    productCardEntries: eligibleProductCards.slice(0, PRODUCT_DISPLAY_LIMIT),
    isDisplayLimited: eligibleProductCards.length > PRODUCT_DISPLAY_LIMIT,
    isCouponsLoading: !mergedCouponMap,
    isProductsLoading: !catalogError && (isCatalogValidating || !productCatalogDto),
  }
}
